import { DeleteOutline, EditOutlined } from "@material-ui/icons";
import {
  BusinessCenterOutlined,
  WarningAmberOutlined,
  WorkOutline,
  CheckCircleOutline,
  HelpOutline,
  PersonOffOutlined,
  RestartAlt,
  ViewAgendaOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Button, Chip, Grid, Paper, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import { exportToExcel } from "../../../helpers/ExportHelpers";
import useConfirm from "../../../hooks/useConfirm";
import useToast from "../../../hooks/useToast";
import { evaluation_refonte_fiche_evaluation_service } from "../../../services/PersonalEvaluationRefonteService";
import FicheDevaluation from "./FicheDevaluation";
import FicheEvaluationForm from "./FicheEvaluationForm";

const INIT_FICHE = {
  ID_FICHE_EVALUATION: 0,
  LIBELE_FICHE: "",
  ID_POSTE: 0,
  ACTIVE: "1",
};

const KPI_PREDICATES = {
  normal: (row) => !!row.is_normal,
  anomalieQuestion: (row) => !!row.has_question_anomaly,
  anomalieSection: (row) => !!row.has_section_anomaly,
  fichesSaisies: (row) => !row.has_no_fiche,
  posteSansFiche: (row) => !!row.has_no_fiche,
  posteAvecFiche: (row) => !row.has_no_fiche,
  anomalies: (row) =>
    !!row.has_question_anomaly || !!row.has_section_anomaly,
};

const getEtatAnomalie = (row) => {
  if (row.has_no_fiche) return "Sans fiche";
  if (row.is_normal) return "Normal";
  if (row.has_question_anomaly && row.has_section_anomaly) {
    return "Questions + Sections";
  }
  return row.has_question_anomaly
    ? "Anomalie questions"
    : "Anomalie sections";
};

const FicheEvaluationTable = ({
  openForm,
  onCloseForm,
  excelExport,
  filtre,
  onResetFilters,
  setActiveKpiParentFilter,
  listFiltersActive,
}) => {
  const [criteres, setCriteres] = useState([]);
  const [current_critere, setCurrentCritere] = useState(INIT_FICHE);
  const [form_open, setFormOpen] = useState(openForm);
  const [fiche_evaluation_form_open, setFicheEvaluationFormOpen] =
    useState(false);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] =
    useState(false);
  const [activeKpiFilter, setActiveKpiFilter] = useState(null);
  const { DialogComponent, openConfirmDialog } = useConfirm();
  const { Toast, showToast } = useToast();

  const handleCloseFormDIalog = () => {
    onCloseForm();
    handleRefreshDataTable(filtre);
    setFormOpen(false);
  };

  const handleEditSelectedCritereGroupee = (critereGroupee) => {
    setCurrentCritere(critereGroupee);
    setFormOpen(true);
  };

  const handleConfirmSuppresion = (critere) => {
    evaluation_refonte_fiche_evaluation_service.delete(critere).then((res) => {
      if (res && res.success) {
        showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
        handleRefreshDataTable(filtre);
      } else {
        showToast(TYPE_NOTIFICATION.ERROR, res.message);
      }
    });
  };

  const handleDeleteSelectedCritereGroupee = (evt, critereGroupee) => {
    evt.preventDefault();
    openConfirmDialog(
      "Confirmation",
      "Voulez vous supprimer cette ligne?",
      () => handleConfirmSuppresion(critereGroupee),
    );
  };

  const handleCLoseFicheDePoste = () => {
    setFicheEvaluationFormOpen(false);
  };

  const handleRefreshDataTable = (filtre) => {
    setOpenLoadingSimpleBackdrop(true);
    evaluation_refonte_fiche_evaluation_service
      .getList(filtre)
      .then((res) => {
        setOpenLoadingSimpleBackdrop(false);
        setCriteres(res.data || []);
      })
      .catch((e) => console.log(e.toString()));
  };

  useEffect(() => {
    if (filtre) {
      handleRefreshDataTable(filtre);
    }
    if (openForm) {
      setCurrentCritere(INIT_FICHE);
      setFormOpen(openForm);
    }
  }, [filtre, openForm, excelExport, current_critere]);

  useEffect(() => {
    if (excelExport) {
      const lignesAffichees = filteredCriteres.map((row) => ({
        ...row,
        ACTIVE: row.ACTIVE === "1" ? "Oui" : "non",
        nbr_question: row.has_no_fiche ? "—" : row.nbr_question,
        nbr_section: row.has_no_fiche ? "—" : row.nbr_section,
        etat_anomalie: getEtatAnomalie(row),
      }));

      exportToExcel(
        lignesAffichees,
        columns,
        "Liste des fiches d'évaluation " + new Date(),
      );
    }
  }, [excelExport]);

  const kpis = useMemo(() => {
    const fonctions = new Set();
    const fichesSaisies = new Set();
    const postesSansFiche = new Set();
    const postesAvecFiche = new Set();
    const fichesAvecAnomalie = new Set();

    const compteurs = criteres.reduce(
      (acc, row) => {
        if (row.FUNCTION_ID !== null && row.FUNCTION_ID !== undefined) {
          fonctions.add(row.FUNCTION_ID);

          if (row.has_no_fiche) {
            postesSansFiche.add(row.FUNCTION_ID);
          } else {
            postesAvecFiche.add(row.FUNCTION_ID);
          }

          if (row.has_question_anomaly || row.has_section_anomaly) {
            fichesAvecAnomalie.add(row.ID_FICHE_EVALUATION);
          }
        }

        if (
          row.ID_FICHE_EVALUATION !== null &&
          row.ID_FICHE_EVALUATION !== undefined
        ) {
          fichesSaisies.add(row.ID_FICHE_EVALUATION);
        }

        return {
          total: acc.total + 1,
          normal: acc.normal + (row.is_normal ? 1 : 0),
          anomalieQuestion:
            acc.anomalieQuestion + (row.has_question_anomaly ? 1 : 0),
          anomalieSection:
            acc.anomalieSection + (row.has_section_anomaly ? 1 : 0),
        };
      },
      { total: 0, normal: 0, anomalieQuestion: 0, anomalieSection: 0 },
    );

    return {
      ...compteurs,
      nombreFonctions: fonctions.size,
      fichesSaisies: fichesSaisies.size,
      postesSansFiche: postesSansFiche.size,
      postesAvecFiche: postesAvecFiche.size,
      fichesAvecAnomalie: fichesAvecAnomalie.size,
    };
  }, [criteres]);

  const filteredCriteres = useMemo(() => {
    if (!activeKpiFilter || !KPI_PREDICATES[activeKpiFilter]) return criteres;
    return criteres.filter(KPI_PREDICATES[activeKpiFilter]);
  }, [criteres, activeKpiFilter]);

  const handleClickKpi = (key) => {
    if (key === "ligneTotal" || key === "nombreFonctions") {
      setActiveKpiFilter(null);
      return;
    }
    setActiveKpiFilter((current) => (current === key ? null : key));
  };

  const handleResetAllFiltres = () => {
    setActiveKpiFilter(null);
    onResetFilters && onResetFilters();
  };

  const columns = [
    {
      field: "LIBELE_FICHE",
      headerName: "Libelé",
      width: 325,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography
            color={params.value === "[Aucun fiche disponible]" ? "red" : ""}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Fonction",
      width: 380,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography>{params.value}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "ACTIVE",
      headerName: "Active ",
      width: 120,
      renderCell: (params) => (
        <Tooltip title={params.value === "1" ? "Oui" : "non"}>
          <Typography>{params.value === "1" ? "Oui" : "non"}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "nbr_question",
      headerName: "Nb. questions",
      width: 130,
      renderCell: (params) => (
        <Typography color={params.row.has_question_anomaly ? "error" : "inherit"}>
          {params.row.has_no_fiche ? "—" : params.value}
        </Typography>
      ),
    },
    {
      field: "nbr_section",
      headerName: "Nb. sections",
      width: 130,
      renderCell: (params) => (
        <Typography color={params.row.has_section_anomaly ? "error" : "inherit"}>
          {params.row.has_no_fiche ? "—" : params.value}
        </Typography>
      ),
    },
    {
      field: "etat_anomalie",
      headerName: "État",
      width: 160,
      renderCell: (params) => {
        const { has_no_fiche, is_normal } = params.row;
        if (has_no_fiche) {
          return <Chip size="small" variant="outlined" label="Sans fiche" color="default" />;
        }
        if (is_normal) {
          return <Chip size="small" variant="outlined" label="Normal" color="success" />;
        }
        return <Chip size="small" variant="outlined" label={getEtatAnomalie(params.row)} color="warning" />;
      },
    },
    {
      field: "CREATION_DATE",
      headerName: "Date de la création ",
      width: 200,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography>{params.value}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "libelle_actions",
      headerName: "Actions",
      disableClickEventBulbing: true,
      renderCell: (params) => {
        return (
          <Grid
            display={
              params.row.LIBELE_FICHE === "[Aucun fiche disponible]"
                ? "none"
                : "flex"
            }
          >
            <Tooltip title={"Cliquer pour Modifier "}>
              <LoadingButton
                onClick={(e) => handleEditSelectedCritereGroupee(params.row)}
                color="secondary"
              >
                <EditOutlined />
              </LoadingButton>
            </Tooltip>
            <Tooltip title={"Cliquer pour Supprimer "}>
              <LoadingButton
                sx={{ display: "none" }}
                onClick={(e) =>
                  handleDeleteSelectedCritereGroupee(e, params.row)
                }
                color="warning"
              >
                <DeleteOutline />
              </LoadingButton>
            </Tooltip>
          </Grid>
        );
      },
      width: 120,
    },
  ];

  useEffect(() => {
    if(activeKpiFilter) {
      setActiveKpiParentFilter(activeKpiFilter);
    }
    if(setActiveKpiParentFilter) {
      setActiveKpiParentFilter();
    }
  }, [ activeKpiFilter, setActiveKpiParentFilter]);

  return (
    <>
      <Toast />
      <DialogComponent />
      <FicheDevaluation
        current_critere_groupe={current_critere}
        isOpen={fiche_evaluation_form_open}
        handleClose={handleCLoseFicheDePoste}
      />
      <FicheEvaluationForm
        ficheEvaluation={current_critere}
        handleClose={handleCloseFormDIalog}
        isOpen={form_open}
      />
      <Grid container spacing={1} mb={1} alignItems="stretch" textAlign="center">
        {[
          { key: "nombreFonctions", label: "Fonctions / postes", value: kpis.nombreFonctions, icon: <BusinessCenterOutlined color="primary" /> },
          { key: "posteAvecFiche", label: "Postes avec fiche", value: kpis.postesAvecFiche, icon: <WorkOutline color="success" /> },
          { key: "posteSansFiche", label: "Postes sans fiche", value: kpis.postesSansFiche, icon: <PersonOffOutlined color="disabled" /> },
          { key: "normal", label: "Normales", value: kpis.normal, icon: <CheckCircleOutline color="success" /> },
        ].map((kpi) => (
          <Grid item xs={12} sm={2.4} key={kpi.label}>
            <Paper
              variant="outlined"
              onClick={() => handleClickKpi(kpi.key)}
              sx={{
                p: 0.5,
                height: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                cursor: "pointer",
                borderColor: activeKpiFilter === kpi.key ? "primary.main" : undefined,
                borderWidth: activeKpiFilter === kpi.key ? 2 : 1,
                backgroundColor: activeKpiFilter === kpi.key ? "action.selected" : undefined,
              }}
            >
              <Typography variant="h6" display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                {kpi.icon}
                {kpi.value}</Typography>
              <Typography variant="caption" color="text.secondary">
                {kpi.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
        <Grid item xs={12} sm={2.4}>
          <Paper
            variant="outlined"
            sx={{
              p: 0.5,
              boxSizing: "border-box",
              textAlign: "center",
              borderColor: ["anomalies", "anomalieQuestion", "anomalieSection"].includes(activeKpiFilter)
                ? "primary.main"
                : undefined,
              borderWidth: ["anomalies", "anomalieQuestion", "anomalieSection"].includes(activeKpiFilter)
                ? 2
                : 1,
            }}
          >
            <Grid
              onClick={() => handleClickKpi("anomalies")}
              sx={{
                pb: 0.5,
                cursor: "pointer",
                backgroundColor: activeKpiFilter === "anomalies" ? "action.selected" : undefined,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                <WarningAmberOutlined color="warning" fontSize="small" />
                {kpis.fichesAvecAnomalie}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                Anomalies
              </Typography>
            </Grid>
            <Grid container sx={{ pt: 0.5, borderTop: "1px solid", borderColor: "divider" }}>
              {[
                { key: "anomalieQuestion", label: "Anomalies questions", value: kpis.anomalieQuestion, icon: <HelpOutline color="warning" fontSize="small" /> },
                { key: "anomalieSection", label: "Anomalies sections", value: kpis.anomalieSection, icon: <ViewAgendaOutlined color="warning" fontSize="small" /> },
              ].map((kpi, index) => (
                <Grid
                  item
                  xs={6}
                  key={kpi.key}
                  onClick={() => handleClickKpi(kpi.key)}
                  sx={{
                    px: 0.5,
                    cursor: "pointer",
                    borderLeft: index === 0 ? "none" : "1px solid",
                    borderColor: "divider",
                    backgroundColor: activeKpiFilter === kpi.key ? "action.selected" : undefined,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                    {kpi.icon}
                    {kpi.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>
                    {kpi.label}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Grid container justifyContent="flex-end" mb={2}>
        <Button
          size="small"
          variant="text"
          startIcon={<RestartAlt />}
          disabled={!activeKpiFilter && !listFiltersActive}
          onClick={handleResetAllFiltres}
        >
          Réinitialiser les filtres
        </Button>
      </Grid>
      <DataGrid
        loading={openLoadingSimpleBackdrop}
        rows={filteredCriteres}
        sx={{ height: 500 }}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[100, 75, 50, 25, 10, 5]}
        disableSelectionOnClick
        getRowId={(row) => row.num}
      />
    </>
  );
};
export default FicheEvaluationTable;
